import { AlertCircle } from "lucide-react";

interface PasswordStrengthMeterProps {
  password: string;
  error: string | null;
}

export const PasswordStrengthMeter = ({ password, error }: PasswordStrengthMeterProps) => {
  // Calculate strength based on length and complexity
  const getStrength = (): { strength: number; color: string } => {
    if (!password) return { strength: 0, color: 'bg-gray-200' };
    if (error) return { strength: 1, color: 'bg-red-500' };
    
    let strength = 0;
    
    // Length check
    if (password.length >= 8) strength += 1;
    if (password.length >= 10) strength += 1;
    if (password.length >= 12) strength += 1;
    
    // Complexity checks
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    // Map strength to color and width
    if (strength <= 2) return { strength: 1, color: 'bg-red-500' };
    if (strength <= 4) return { strength: 2, color: 'bg-yellow-500' };
    if (strength <= 6) return { strength: 3, color: 'bg-blue-500' };
    return { strength: 4, color: 'bg-green-500' };
  };
  
  const { strength, color } = getStrength();
  
  return (
    <div className="space-y-2">
      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${
            !password ? 'w-0' : 
            strength === 1 ? 'w-1/4 ' + color : 
            strength === 2 ? 'w-2/4 ' + color : 
            strength === 3 ? 'w-3/4 ' + color : 
            'w-full ' + color
          }`}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {error ? (
          <span className="text-red-500 flex items-center gap-1">
            <AlertCircle size={12} />
            {error}
          </span>
        ) : (
          "Use at least 8 characters with a mix of letters, numbers, and symbols"
        )}
      </p>
    </div>
  );
}; 